import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, ShieldCheck, Loader2 } from "lucide-react";
import { ReactorMark } from "@/components/command-center/hud/ReactorAssembly";
import { OwnerEnrollment } from "@/components/OwnerEnrollment";
import { OwnerRecovery } from "@/components/OwnerRecovery";
import { OwnerPasswordRemoval } from "@/components/OwnerPasswordRemoval";
import { authRequest } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function LockScreen() {
  const { unlock } = useAuth();
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [setup, setSetup] = useState(null);
  const [bootstrap, setBootstrap] = useState("");
  const [state, setState] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    authRequest("/auth/state", undefined, undefined, controller.signal).then(setState)
      .catch((failure) => { if (!controller.signal.aborted) setError(failure.message); });
    return () => controller.abort();
  }, []);
  useEffect(() => {
    if (!setup) return;
    const timer = setTimeout(() => { setSetup(null); setBootstrap(""); setCode(""); setError("Enrollment expired. Start private setup again."); }, setup.expires_in * 1000);
    return () => clearTimeout(timer);
  }, [setup]);
  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError("");
    if (state?.password_required && !password) { setError("Enter your owner password."); return; }
    if (!/^\d{6}$/.test(code)) { setError("Enter a fresh six-digit authenticator code."); return; }
    setBusy(true);
    try {
      await authRequest("/auth/verify", { code, password: state?.password_required ? password : undefined, enrollment_id: setup?.enrollment_id }, setup ? bootstrap.trim() : undefined);
      await unlock();
      setBootstrap(""); setSetup(null); setCode(""); setPassword("");
    } catch (failure) {
      setError(failure.message); setCode(""); setPassword("");
      authRequest("/auth/state").then(setState).catch(() => {});
    }
    finally { setBusy(false); }
  };
  const startSetup = async () => {
    setBusy(true); setError("");
    try { setSetup(await authRequest("/auth/provision", { password: state?.password_required ? password : undefined }, bootstrap.trim())); }
    catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] jv-grid flex flex-col items-center p-4 overflow-y-auto">
      <div className="pointer-events-none fixed inset-0 jv-grid-lines opacity-50" />
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="jv-panel relative z-10 w-full max-w-md p-6 flex flex-col items-center my-auto rr-block ph-no-capture"
        data-private="true"
        data-testid="lock-screen"
      >
        <div className="-mt-2 mb-3">
          <ReactorMark />
        </div>
        <div className="font-mono text-2xl font-bold tracking-[0.4em] text-jarvis-cyan jv-glow">JARVIS</div>
        <div className="flex items-center gap-1.5 mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-jarvis-amber">
          <Lock className="w-3 h-3" /> Locked
        </div>

        <form onSubmit={submit} className="w-full mt-6 space-y-3" data-testid="owner-login-form">
          {state?.password_required && <>
            <label htmlFor="lock-password" className="block text-xs text-slate-400">Owner password</label>
            <input id="lock-password" data-testid="lock-password-input" type="password" autoComplete="current-password" required disabled={busy}
              value={password} onChange={(event) => setPassword(event.target.value)}
              className="w-full min-h-11 bg-black/40 border border-jarvis-cyan/25 rounded-md px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-jarvis-cyan/60" />
          </>}
          <label htmlFor="lock-code" className="block text-xs text-slate-400">Authenticator code</label>
          <input
            id="lock-code"
            aria-label="Unlock code"
            data-testid="lock-code-input"
            autoFocus
            inputMode="numeric"
            maxLength={6}
            type="password"
            autoComplete="one-time-code"
            disabled={busy || !state || (state.setup_required && !setup)}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="• • • • • •"
            className="w-full text-center tracking-[0.6em] bg-black/40 border border-jarvis-cyan/25 rounded-md px-4 py-3 text-lg font-code text-jarvis-cyanBright placeholder:text-slate-700 focus:outline-none focus:border-jarvis-cyan/60 focus:shadow-[0_0_20px_rgba(0,240,255,0.2)]"
          />
          {error && <p className="font-mono text-[11px] text-jarvis-red text-center" data-testid="lock-error">{error}</p>}
          <button
            type="submit"
            disabled={busy || code.length !== 6 || !state || (state.password_required && !password) || (state.setup_required && !setup)}
            data-testid="lock-submit"
            className="w-full flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.2em] py-3 rounded-md bg-jarvis-cyan/15 border border-jarvis-cyan/40 text-jarvis-cyan hover:bg-jarvis-cyan/25 hover:shadow-[0_0_16px_rgba(0,240,255,0.3)] transition-all disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            {state?.password_required || state?.auth_mode === "totp_only" ? "Unlock" : "Continue to private setup"}
          </button>
        </form>

        {state?.setup_required && state.password_required && <p className="mt-4 text-xs text-jarvis-amber" data-testid="interrupted-enrollment-notice">Authenticator replacement is incomplete. Enter your unchanged owner password above and your private setup key below to create another QR. Old authenticator codes cannot unlock JARVIS.</p>}
        {state?.setup_required && <OwnerEnrollment bootstrap={bootstrap} setBootstrap={setBootstrap} setup={setup} busy={busy} onStart={startSetup}
          onCancel={() => { setSetup(null); setBootstrap(""); setCode(""); }} />}
        {state?.activated && state.auth_mode !== "totp_only" && !state.password_required && <p className="mt-4 text-xs text-jarvis-amber text-center" data-testid="password-migration-notice">Use your existing authenticator to open private password setup. Your dashboard stays locked until setup is complete.</p>}
        {state?.activated && <p className="mt-4 text-xs text-slate-400 text-center" data-testid="session-policy">{state.auth_mode === "totp_only" ? "Authenticator only · password disabled" : "Owner password + authenticator"} · 24-hour remembered sessions · closing or refreshing does not sign you out</p>}
        {state?.activated && state.auth_mode !== "totp_only" && <OwnerPasswordRemoval />}
        <OwnerRecovery />
        {!state && <button data-testid="auth-state-retry" onClick={() => { setError(""); authRequest("/auth/state").then(setState).catch((failure) => setError(failure.message)); }} className="mt-4 text-xs text-jarvis-cyan min-h-11">Retry connection</button>}
      </motion.div>
    </div>
  );
}
